import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import {
    CodeBlock, BlockType, SettingType, ValidationMessage, ValidationMessageType, SettingState,
} from '@appblocks/codegeneration/types';
import Setting from '@appblocks/codegeneration/SettingsConfigurations/settings/Setting';
import * as micropython from './micropython';
import * as tios from './tios';
import * as zephyr from './zephyr';
import * as simulator from './simulator';

export default class Stg extends BaseFeature {
    constructor() {
        super();
        this.id = 'stg';
        this.name = 'Settings';
        this.priority = 1000;
        this.icon = 'MdOutlineSettingsInputComponent';
        // this.requirements = ['tios'];
        this.fileLinks = [
            // 'settings\\settings.tbs',
            // 'settings\\settings.tbh',
        ];
        this.files = [
            'settings.xtxt',
            'libraries/settings/settings.tbh',
            'libraries/settings/settings.tbs',
        ];

        const settings = [
            {
                id: 'ENABLED',
                name: 'Settings Enabled',
                settingType: SettingType.OPTION,
                required: true,
                default: '0',
                options: [
                    {
                        name: 'Disabled',
                        value: '0',
                    },
                    {
                        name: 'Enabled',
                        value: '1',
                    },
                ],
            },
            {
                id: 'STG_DEBUG_PRINT',
                name: 'Debug Printing',
                settingType: SettingType.OPTION,
                required: true,
                default: '0',
                options: [
                    {
                        name: 'Enabled',
                        value: '1',
                    },
                    {
                        name: 'Disabled',
                        value: '0',
                    },
                ],
                helpText: 'When this option is enabled, the values of settings pertaining to the STG library are printed to the logging display (selected in the General features page) when they are changed.',
            },
            {
                id: 'STORAGE',
                name: 'Storage Type',
                settingType: SettingType.HIDDEN,
                required: true,
                readonly: true,
                default: 'EEPROM',
                options: [
                    {
                        name: 'Use EEPROM',
                        value: 'EEPROM',
                    },
                    {
                        name: 'Use Flash Disk',
                        value: 'FLASH',
                    },
                ],
            },
            {
                id: 'STG_TIMESTAMP',
                name: 'Include Timestamp',
                settingType: SettingType.HIDDEN,
                required: true,
                default: '0',
                readonly: true,
                options: [
                    {
                        name: 'True',
                        value: '1',
                    },
                    {
                        name: 'False',
                        value: '0',
                    },
                ],
            },
            {
                id: 'REDUNDANCY',
                name: 'Redundancy',
                settingType: SettingType.HIDDEN,
                required: true,
                default: '0',
                options: [
                    {
                        name: 'No Redundancy',
                        value: '0',
                    },
                    {
                        name: 'Two Copies for EEPROM',
                        value: '1',
                    },
                    {
                        name: 'Two Copies for All',
                        value: '2',
                    },
                ],
            },
            {
                id: 'STG_MAX_NUM_SETTINGS',
                name: 'Max number of settings',
                settingType: SettingType.HIDDEN,
                required: true,
                default: '0',
            },
            {
                id: 'STG_RAM_ARRAY_SIZE',
                name: 'Ram Array Size',
                settingType: SettingType.HIDDEN,
                required: true,
                default: '0',
            },
            {
                id: 'STG_MAX_SETTING_NAME_LEN',
                name: 'Max Name Length',
                settingType: SettingType.HIDDEN,
                required: true,
                default: '0',
            },
            {
                id: 'STG_MAX_SETTING_VALUE_LEN',
                name: 'Max Value Length',
                settingType: SettingType.HIDDEN,
                required: true,
                default: '0',
            },
            {
                id: 'SETTINGS',
                name: 'Settings',
                settingType: SettingType.ARRAY,
                required: true,
                refId: 'stg_setting',
                default: [] as BaseFeature[],
                helpText: 'Settings are used in the Web Dashboard, LUIS BLE Dashboard, DS Manager and LCD Menus features.',
            },
        ];
        this.settings = this.settings.concat(settings);
    }

    getEnabled(): boolean {
        return this.getSettingValue('SETTINGS').length > 0 || this.getSettingValue('ENABLED') === '1';
    }

    getDependencies() {
        const dependencies = [];
        if (this.getSettingValue('STORAGE') === 'FLASH') {
            dependencies.push('fd');
        }
        return dependencies;
    }

    getFiles(): string[] {
        switch (this.project.runtime) {
            case 'micropython':
                return micropython.getFiles(this);
            default:
                return tios.getFiles(this);
        }
    }

    getCodeBlocks(): CodeBlock[] {
        switch (this.project.runtime) {
            case 'micropython':
                return micropython.getCodeBlocks(this);
            case 'zephyr':
                return zephyr.getCodeBlocks(this);
            case 'simulator':
                return simulator.getCodeBlocks(this);
            case 'tios':
                return tios.getCodeBlocks(this);
            default:
                return [];
        }
    }

    getDetails(): string {
        return `
## Settings
Settings are used to store and retrieve data from the device, and are stored in non-volatile memory. 
Settings can be used to store configuration data, calibration data, or any other data that needs to be stored on the device.
Settings are linked to variables, and when writing to these variables, the settings are automatically updated.

You can allow for configuring settings from a number of interfaces:
* [Web Dashboard](#features/webdashboard)
* [LUIS BLE Dashboard](#features/luis)
* [DS Manager](#features/ds_man)
* [LCD Menus](#features/lcd_menu)
        `;
    }

    getDocURL(): string {
        return `${process.env.HOME_URL}/docs/features/settings`;
    }
}
